import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { CheckCircle2 } from 'lucide-react';

import { useAccountsStore } from '@/store';
import {
  useAutoNominate,
  useAutoSelection,
  useStakingPosition,
  useTx,
  invalidateAutoNominateCache,
  isLedgerAddress,
} from '@/hooks';
import { TopBar } from '@/components/layout';
import { AddressLabel, LoadingIndicator } from '@/components/ui';
import { ValidatorPickerSheet } from './ValidatorPickerSheet';
import { SignerConfirmCard } from './SignerConfirmCard';
import { AutoNominateBlock } from './AutoNominateBlock';

/**
 * Change validators (re-nominate).
 *
 * Submits `staking.nominate(newTargets)` against the account's
 * existing bond. No bondExtra, no chill — just replaces the nomination
 * set in one extrinsic. Cheap (~0.016 XX) and instant in effect from
 * the next era.
 *
 * Reuses StartStaking's auto-nominate selection and ValidatorPickerSheet,
 * with the hand-pick path pre-seeded with the user's current
 * nominations so they can tweak rather than restart.
 */
export function ChangeValidators() {
  const navigate = useNavigate();
  const { accounts, activeAddress } = useAccountsStore();
  const activeAccount = useMemo(
    () => accounts.find((a) => a.address === activeAddress) ?? accounts[0],
    [accounts, activeAddress]
  );

  const { position } = useStakingPosition(activeAccount?.address ?? null);
  const {
    result: autoResult,
    isComputing: autoComputing,
    error: autoError,
    refresh,
  } = useAutoNominate(activeAccount?.address ?? null);
  const { submit, status, error: txError } = useTx();

  // This screen serves two states: re-nominating an account that already
  // nominates, and the first nomination for an account that is bonded but
  // not nominating (e.g. after a chill). Both submit staking.nominate();
  // the only difference is wording.
  const nominating = !!position?.isNominating;
  const isBonded = !!position?.ledger;
  // Auto-pick after the user's optional quality levers (no-op by default).
  const autoSelection = useAutoSelection(autoResult);

  // Form state — initial hand-pick selection is the user's current nominations
  const [mode, setMode] = useState<'auto' | 'pick'>('auto');
  const [handPicked, setHandPicked] = useState<string[]>([]);
  const [handPickedInitialised, setHandPickedInitialised] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Initialise hand-pick from position.targets once position loads.
  // We don't want to clobber a subsequent edit, so this only fires once.
  useEffect(() => {
    if (handPickedInitialised) return;
    if (position?.targets && position.targets.length > 0) {
      setHandPicked(position.targets);
      setHandPickedInitialised(true);
    }
  }, [position, handPickedInitialised]);

  const targets = useMemo(
    () =>
      mode === 'auto'
        ? autoSelection.selected.map((v) => v.validatorId)
        : handPicked,
    [mode, autoSelection.selected, handPicked]
  );

  const targetsValid = targets.length > 0 && targets.length <= 16;
  const currentTargets = useMemo(
    () => new Set(position?.targets ?? []),
    [position]
  );
  const targetsChanged = useMemo(() => {
    if (targets.length !== currentTargets.size) return true;
    return targets.some((t) => !currentTargets.has(t));
  }, [targets, currentTargets]);

  const isLedger = isLedgerAddress(activeAccount?.address ?? '');
  const isSubmitting =
    status === 'signing' || status === 'broadcasting' || status === 'in-block';
  const isDone = status === 'finalized';
  const canSubmit =
    targetsValid &&
    targetsChanged &&
    (isLedger || password.length > 0) &&
    (status === 'idle' || status === 'error');

  useEffect(() => {
    if (status !== 'finalized') return;
    invalidateAutoNominateCache();
    const t = setTimeout(() => navigate('/staking'), 1200);
    return () => clearTimeout(t);
  }, [status, navigate]);

  if (!activeAccount) return null;

  const handleSubmit = async () => {
    if (!canSubmit || !activeAccount) return;
    setPasswordError(null);
    try {
      await submit(
        (api) => api.tx.staking.nominate(targets),
        { address: activeAccount.address, password }
      );
    } catch (err) {
      const msg = (err as Error).message.toLowerCase();
      if (
        msg.includes('password') ||
        msg.includes('unable to decode') ||
        msg.includes('incorrect')
      ) {
        setPasswordError('Incorrect password. Please try again.');
      }
    }
  };

  return (
    <>
      <TopBar title={nominating ? 'Change validators' : 'Nominate validators'} showBack />
      <div className="px-5 py-4 space-y-4">
        {/* Account context */}
        <div className="card space-y-2">
          <p className="text-xs uppercase tracking-wider text-ink-300 font-medium">
            {nominating ? 'Re-nominating from' : 'Nominating from'}
          </p>
          <AddressLabel address={activeAccount.address} className="text-sm" />
          {nominating && position?.targets && (
            <p className="text-xs text-ink-300">
              Currently nominating{' '}
              <span className="text-ink-200">
                {position.targets.length}
              </span>{' '}
              validator{position.targets.length === 1 ? '' : 's'}
            </p>
          )}
        </div>

        {!position && (
          <LoadingIndicator message="Loading your position..." />
        )}

        {position && !isBonded && (
          <div className="card">
            <p className="text-sm text-ink-200">
              This account isn't bonded yet. Bond and nominate together in
              Start staking first.
            </p>
          </div>
        )}

        {position && isBonded && !isDone && (
          <>
            {/* Selection */}
            <div className="card space-y-3">
              <label className="text-xs uppercase tracking-wider text-ink-300 font-medium">
                New validator set
              </label>

              <div className="flex p-1 rounded-xl bg-ink-950 border border-ink-800 gap-1">
                {(['auto', 'pick'] as const).map((m) => (
                  <button
                    key={m}
                    onClick={() => setMode(m)}
                    className={clsx(
                      'flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors',
                      mode === m
                        ? 'bg-ink-800 text-xx-500'
                        : 'text-ink-300 active:bg-ink-800/50'
                    )}
                  >
                    {m === 'auto' ? 'Auto-recommend' : 'Hand-pick'}
                  </button>
                ))}
              </div>

              {mode === 'auto' ? (
                <AutoNominateBlock
                  autoComputing={autoComputing}
                  autoError={autoError}
                  autoResult={autoResult}
                  onRefresh={refresh}
                />
              ) : (
                <PickBlock
                  handPicked={handPicked}
                  onOpenPicker={() => setPickerOpen(true)}
                />
              )}
            </div>

            {/* Review */}
            {targetsValid && (
              <div className="card space-y-2">
                <p className="text-xs uppercase tracking-wider text-ink-300 font-medium">
                  Review
                </p>
                <ReviewRow
                  label="Before"
                  value={`${position.targets.length} validator${position.targets.length === 1 ? '' : 's'}`}
                />
                <ReviewRow
                  label="After"
                  value={`${targets.length} validator${targets.length === 1 ? '' : 's'}`}
                />
                <ReviewRow label="Network fee" value="~0.016 XX" />
                {!targetsChanged && (
                  <p className="text-xs text-ink-300 pt-1">
                    Selection matches your current nominations — change it
                    before signing.
                  </p>
                )}
              </div>
            )}

            {/* Signer confirmation — password or confirm-on-device */}
            {targetsValid && targetsChanged && (
              <SignerConfirmCard
                isLedger={isLedger}
                idPrefix="change"
                password={password}
                onPasswordChange={(v) => {
                  setPassword(v);
                  setPasswordError(null);
                }}
                passwordError={passwordError}
                disabled={isSubmitting}
                waiting={status === 'signing'}
              />
            )}

            {/* CTA */}
            <button
              onClick={handleSubmit}
              disabled={!canSubmit || isSubmitting}
              className={clsx(
                'w-full py-3 rounded-2xl text-sm font-medium transition-opacity',
                canSubmit && !isSubmitting
                  ? 'bg-xx-500 text-ink-950 active:opacity-80'
                  : 'bg-ink-800 text-ink-500 cursor-not-allowed'
              )}
            >
              {submitLabel(status, targets.length, isLedger)}
            </button>
          </>
        )}

        {isDone && (
          <div className="card flex flex-col items-center text-center gap-3 py-6">
            <CheckCircle2 size={36} className="text-success" />
            <p className="font-display font-medium text-sm text-ink-100">
              Nominations updated
            </p>
            <p className="text-xs text-ink-300">Returning to staking…</p>
          </div>
        )}

        {txError && !passwordError && (
          <div className="card">
            <p className="text-sm text-danger">{txError.message}</p>
          </div>
        )}
      </div>

      <ValidatorPickerSheet
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        initial={handPicked}
        onConfirm={(selected) => {
          setHandPicked(selected);
          setPickerOpen(false);
        }}
      />
    </>
  );
}

function submitLabel(
  status: string,
  count: number,
  isLedger: boolean
): string {
  if (status === 'signing')
    return isLedger ? 'Confirm on your Ledger…' : 'Signing…';
  if (status === 'broadcasting') return 'Sending to network…';
  if (status === 'in-block') return 'Waiting for finality…';
  return `Nominate ${count} validator${count === 1 ? '' : 's'}`;
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-xs text-ink-300">{label}</span>
      <span className="font-mono text-sm text-ink-100 numeric">{value}</span>
    </div>
  );
}

function PickBlock({
  handPicked,
  onOpenPicker,
}: {
  handPicked: string[];
  onOpenPicker: () => void;
}) {
  return (
    <div className="space-y-2">
      {handPicked.length === 0 ? (
        <p className="text-sm text-ink-300">
          No validators selected yet. Tap below to choose.
        </p>
      ) : (
        <>
          <p className="text-sm text-ink-100">
            {handPicked.length} validator{handPicked.length === 1 ? '' : 's'}{' '}
            selected
          </p>
          <ul className="space-y-1">
            {handPicked.slice(0, 5).map((address, idx) => (
              <li
                key={address}
                className="flex items-center gap-2 py-1 text-xs"
              >
                <span className="text-ink-300 w-5 flex-shrink-0">{idx + 1}</span>
                <AddressLabel address={address} className="text-xs min-w-0" />
              </li>
            ))}
            {handPicked.length > 5 && (
              <li className="text-xs text-ink-300 pl-7">
                + {handPicked.length - 5} more
              </li>
            )}
          </ul>
        </>
      )}
      <button
        onClick={onOpenPicker}
        className="text-sm text-xx-500 active:opacity-70"
      >
        {handPicked.length === 0 ? 'Choose validators →' : 'Edit selection →'}
      </button>
    </div>
  );
}
